import React, { useState } from 'react';
import { BarChart3Icon, CalendarIcon, BatteryChargingIcon, BeakerIcon, DatabaseIcon, GaugeIcon, XIcon, FilterIcon, LineChart, TrendingUpIcon, Loader2Icon, ThermometerIcon, FlaskConicalIcon, ScaleIcon } from 'lucide-react';
import { ResponsiveLine } from '@nivo/line';
import { DownloadIcon } from 'lucide-react';
interface AnalyticsProps {
  onClose: () => void;
  orders: Array<{
    id: string;
    name: string;
    charge: string;
    completionDate: string;
    menge: string;
  }>;
}
export function Analytics({
  onClose,
  orders
}: AnalyticsProps) {
  const [dateRange, setDateRange] = useState({
    start: '2025-04-15',
    end: '2025-04-22'
  });
  const [selectedOrders, setSelectedOrders] = useState(orders);
  // Sample data for visualizations
  const powerConsumption = [{
    date: '15.04',
    value: 2840
  }, {
    date: '16.04',
    value: 2920
  }, {
    date: '17.04',
    value: 2780
  }, {
    date: '18.04',
    value: 2850
  }, {
    date: '19.04',
    value: 2760
  }, {
    date: '20.04',
    value: 2890
  }, {
    date: '21.04',
    value: 2950
  }, {
    date: '22.04',
    value: 2830
  }];
  const pearlWear = [{
    machine: 'Perlmühle 1',
    initial: 630,
    current: 575
  }, {
    machine: 'Perlmühle 2',
    initial: 575,
    current: 540
  }, {
    machine: 'Perlmühle 3',
    initial: 575,
    current: 552
  }, {
    machine: 'Perlmühle 4',
    initial: 1150,
    current: 1105
  }];
  const tankLevelTrends = {
    labels: ['15.04', '16.04', '17.04', '18.04', '19.04', '20.04', '21.04', '22.04'],
    data: {
      'Tank 1': [33.5, 32.8, 34.2, 33.7, 32.9, 33.4, 33.8, 33.2],
      'Tank 2': [42.4, 41.8, 40.5, 39.9, 41.2, 40.8, 41.5, 42.1],
      'Tank 3': [43.1, 43.1, 43.1, 43.1, 43.1, 43.1, 43.1, 43.1]
    }
  };
  const machineTemperatures = {
    labels: ['15.04', '16.04', '17.04', '18.04', '19.04', '20.04', '21.04', '22.04'],
    data: {
      'Perlmühle 1': [52.5, 53.1, 52.8, 53.2, 52.9, 53.4, 53.0, 52.7],
      'Perlmühle 2': [54.5, 54.8, 54.3, 55.1, 54.7, 54.9, 55.2, 54.6],
      'Perlmühle 3': [55.4, 55.7, 55.2, 56.0, 55.8, 55.5, 55.9, 55.6],
      'Perlmühle 4': [57.6, 57.9, 57.4, 58.2, 57.8, 58.0, 58.3, 57.7]
    }
  };
  const machineEfficiency = {
    'Perlmühle 1': {
      current: 98,
      target: 100
    },
    'Perlmühle 2': {
      current: 97,
      target: 100
    },
    'Perlmühle 3': {
      current: 99,
      target: 100
    },
    'Perlmühle 4': {
      current: 96,
      target: 100
    }
  };
  const additiveConsumption = {
    labels: ['15.04', '16.04', '17.04', '18.04', '19.04', '20.04', '21.04', '22.04'],
    data: {
      Entschäumer: [0.3, 0.4, 0.3, 0.5, 0.3, 0.4, 0.3, 0.4],
      Dispergier: [0.5, 0.6, 0.4, 0.5, 0.4, 0.5, 0.6, 0.5]
    }
  };
  const tankUtilization = {
    labels: ['15.04', '16.04', '17.04', '18.04', '19.04', '20.04', '21.04', '22.04'],
    ansatzTanks: {
      AT1: [15.8, 16.2, 15.7, 15.9, 15.7, 15.8, 16.0, 15.8],
      AT2: [12.9, 13.1, 12.8, 13.0, 12.7, 12.9, 13.1, 12.9]
    },
    lagerTanks: {
      'Tank 1': [33.5, 32.8, 34.2, 33.7, 32.9, 33.4, 33.8, 33.2],
      'Tank 2': [42.4, 41.8, 40.5, 39.9, 41.2, 40.8, 41.5, 42.1],
      'Tank 3': [43.1, 43.1, 43.1, 43.1, 43.1, 43.1, 43.1, 43.1] // Values in tons
    }
  };
  const qualityTests = [{
    date: '15.04',
    ph: 7.2,
    viscosity: 980,
    consistency: 'Optimal'
  }, {
    date: '16.04',
    ph: 7.1,
    viscosity: 975,
    consistency: 'Leicht erhöht'
  }, {
    date: '17.04',
    ph: 7.3,
    viscosity: 985,
    consistency: 'Optimal'
  }, {
    date: '18.04',
    ph: 7.2,
    viscosity: 970,
    consistency: 'Optimal'
  }, {
    date: '19.04',
    ph: 7.1,
    viscosity: 990,
    consistency: 'Leicht erhöht'
  }, {
    date: '20.04',
    ph: 7.2,
    viscosity: 980,
    consistency: 'Optimal'
  }, {
    date: '21.04',
    ph: 7.3,
    viscosity: 975,
    consistency: 'Optimal'
  }, {
    date: '22.04',
    ph: 7.2,
    viscosity: 985,
    consistency: 'Optimal'
  }];
  const updateDateRange = (start: string, end: string) => {
    setDateRange({
      start,
      end
    });
    // Filter orders based on date range
    const filteredOrders = orders.filter(order => {
      const orderDate = new Date(order.completionDate.split('.').reverse().join('-'));
      const startDate = new Date(start);
      const endDate = new Date(end);
      return orderDate >= startDate && orderDate <= endDate;
    });
    setSelectedOrders(filteredOrders);
  };
  const calculateTotalProduction = () => {
    return selectedOrders.reduce((total, order) => total + parseFloat(order.menge), 0);
  };
  const calculateAveragePowerConsumption = () => {
    return powerConsumption.reduce((acc, curr) => acc + curr.value, 0) / powerConsumption.length;
  };
  const calculateTotalPearlWear = () => {
    return pearlWear.reduce((acc, curr) => acc + (curr.initial - curr.current), 0);
  };
  const calculateAverageAdditiveConsumption = () => {
    const totalDays = additiveConsumption.labels.length;
    return {
      Entschäumer: additiveConsumption.data['Entschäumer'].reduce((a, b) => a + b, 0) / totalDays,
      Dispergier: additiveConsumption.data['Dispergier'].reduce((a, b) => a + b, 0) / totalDays
    };
  };
  const calculateAverageTankUtilization = () => {
    const allTanks = [...Object.values(tankUtilization.Ansatztanks), ...Object.values(tankUtilization.Lagertanks)];
    return allTanks.reduce((acc, tank) => acc + tank.utilization, 0) / allTanks.length;
  };
  // Transform power consumption data for Nivo
  const powerConsumptionData = [{
    id: 'Power Consumption',
    data: powerConsumption.map(entry => ({
      x: entry.date,
      y: entry.value
    }))
  }];
  return <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" data-id="element-13">
      <div className="bg-white rounded-lg w-[90vw] h-[90vh] max-w-7xl overflow-hidden flex flex-col" data-id="element-14">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-15">
          <div className="flex items-center gap-2" data-id="element-16">
            <BarChart3Icon className="text-[#3498dd]" size={20} data-id="element-17" />
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-18">
              Produktionsauswertung
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-19">
            <XIcon size={20} data-id="element-20" />
          </button>
        </div>
        <div className="p-6 flex-1 overflow-auto" data-id="element-21">
          {/* Date Range Selector */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-22">
            <div className="flex items-center justify-between" data-id="element-23">
              <div className="flex items-center gap-4" data-id="element-24">
                <CalendarIcon className="text-[#3498dd]" size={20} data-id="element-25" />
                <div className="grid grid-cols-2 gap-4" data-id="element-26">
                  <div data-id="element-27">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-28">
                      Von
                    </label>
                    <input type="date" value={dateRange.start} onChange={e => updateDateRange(e.target.value, dateRange.end)} className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-29" />
                  </div>
                  <div data-id="element-30">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-31">
                      Bis
                    </label>
                    <input type="date" value={dateRange.end} onChange={e => updateDateRange(dateRange.start, e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-32" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-33">
                <button onClick={() => {
                // Export functionality would go here
                alert('Export wird implementiert');
              }} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors" data-id="element-34">
                  <DownloadIcon size={16} data-id="element-35" />
                  Export
                </button>
                <div className="flex items-center gap-2" data-id="element-36">
                  <FilterIcon size={16} className="text-gray-500" data-id="element-37" />
                  <span className="text-sm text-gray-600" data-id="element-38">
                    {selectedOrders.length} Aufträge ausgewählt
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Enhanced Dashboard Boxes */}
          <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-39">
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-40">
              <div className="flex items-center gap-2 mb-2" data-id="element-41">
                <DatabaseIcon size={16} className="text-[#3498dd]" data-id="element-42" />
                <h4 className="text-sm font-medium text-gray-700" data-id="element-43">
                  Gesamtproduktion
                </h4>
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-44">
                {calculateTotalProduction().toFixed(1)}t
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-45">
              <div className="flex items-center gap-2 mb-2" data-id="element-46">
                <BatteryChargingIcon size={16} className="text-[#3498dd]" data-id="element-47" />
                <h4 className="text-sm font-medium text-gray-700" data-id="element-48">
                  Ø Stromverbrauch
                </h4>
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-49">
                {calculateAveragePowerConsumption().toFixed(0)} kWh
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-50">
              <div className="flex items-center gap-2 mb-2" data-id="element-51">
                <BeakerIcon size={16} className="text-[#3498dd]" data-id="element-52" />
                <h4 className="text-sm font-medium text-gray-700" data-id="element-53">
                  Perlenverbrauch
                </h4>
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-54">
                {calculateTotalPearlWear()} kg
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-55">
              <div className="flex items-center gap-2 mb-2" data-id="element-56">
                <Loader2Icon size={16} className="text-[#3498dd]" data-id="element-57" />
                <h4 className="text-sm font-medium text-gray-700" data-id="element-58">
                  Ø Maschineneffizienz
                </h4>
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-59">
                97.5<span className="text-sm font-normal text-gray-500" data-id="element-60">%</span>
              </div>
            </div>
          </div>
          {/* Enhanced Charts Section */}
          <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-61">
            {/* Power Consumption Chart */}
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-62">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-63">
                <BatteryChargingIcon size={16} className="text-[#3498dd]" data-id="element-64" />
                Stromverbrauch
              </h4>
              <div className="h-64" data-id="element-65">
                <ResponsiveLine data={powerConsumptionData} margin={{
                top: 20,
                right: 20,
                bottom: 40,
                left: 60
              }} xScale={{
                type: 'point'
              }} yScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto'
              }} curve="natural" axisTop={null} axisRight={null} axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0
              }} axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0
              }} enablePoints={true} pointSize={8} pointColor="#ffffff" pointBorderWidth={2} pointBorderColor="#3498dd" enableGridX={true} enableGridY={true} gridXValues={5} gridYValues={5} colors={['#3498dd']} lineWidth={3} enableArea={true} areaOpacity={0.1} enableSlices="x" animate={true} motionConfig="gentle" gridYValues={5} theme={{
                grid: {
                  line: {
                    stroke: '#e2e8f0',
                    strokeWidth: 1,
                    strokeOpacity: 0.1
                  }
                },
                crosshair: {
                  line: {
                    stroke: '#3498dd',
                    strokeWidth: 1,
                    strokeOpacity: 0.35
                  }
                }
              }} defs={[{
                id: 'gradient',
                type: 'linearGradient',
                colors: [{
                  offset: 0,
                  color: '#3498dd',
                  opacity: 0.2
                }, {
                  offset: 100,
                  color: '#3498dd',
                  opacity: 0
                }]
              }]} fill={[{
                match: '*',
                id: 'gradient'
              }]} tooltip={({
                point
              }) => <div className="bg-white px-3 py-2 border border-gray-200 rounded shadow-sm" data-id="element-67">
                      <strong className="text-gray-700" data-id="element-68">{point.data.x}</strong>
                      <div className="text-[#3498dd]" data-id="element-69">{point.data.y} kWh</div>
                    </div>} data-id="element-66" />
              </div>
            </div>
            {/* Tank Level Trends */}

            {/* Machine Temperature Analysis */}
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-70">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-71">
                <ThermometerIcon size={16} className="text-[#3498dd]" data-id="element-72" />
                Temperaturverlauf
              </h4>
              <div className="h-64" data-id="element-73">
                <div className="space-y-4" data-id="element-74">
                  {Object.entries(machineTemperatures.data).map(([machine, temps], index) => <div key={machine} data-id="element-75">
                        <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-76">
                          <span data-id="element-77">{machine}</span>
                          <span className={temps[temps.length - 1] > 55 ? 'text-red-600' : 'text-green-600'} data-id="element-78">
                            {temps[temps.length - 1]}°C
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-79">
                          <div className={`h-full rounded-full ${temps[temps.length - 1] > 55 ? 'bg-red-500' : 'bg-green-500'}`} style={{
                      width: `${temps[temps.length - 1] / 60 * 100}%`
                    }} data-id="element-80" />
                        </div>
                      </div>)}
                </div>
              </div>
            </div>
            {/* Additive Consumption Chart */}
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-81">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-82">
                <BeakerIcon size={16} className="text-[#3498dd]" data-id="element-83" />
                Zusatzstoffverbrauch
              </h4>
              <div className="h-64" data-id="element-84">
                <div className="h-full" data-id="element-85">
                  {Object.entries(additiveConsumption.data).map(([additive, values], index) => <div key={additive} className="mb-4" data-id="element-86">
                        <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-87">
                          <span data-id="element-88">{additive}</span>
                          <span data-id="element-89">{values[values.length - 1]}t/Tag</span>
                        </div>
                        <div className="flex h-8 items-end gap-1" data-id="element-90">
                          {values.map((value, i) => <div key={i} className="flex-1 bg-blue-100 rounded-t" style={{
                      height: `${value / Math.max(...values) * 100}%`,
                      opacity: 0.6 + 0.4 * (index === 0 ? 1 : 0.7)
                    }} data-id="element-91" />)}
                        </div>
                        <div className="flex justify-between mt-1" data-id="element-92">
                          <span className="text-xs text-gray-400" data-id="element-93">
                            {additiveConsumption.labels[0]}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-94">
                            {additiveConsumption.labels[additiveConsumption.labels.length - 1]}
                          </span>
                        </div>
                      </div>)}
                </div>
              </div>
            </div>
            {/* Quality Tests Analysis */}
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-95">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-96">
                <FlaskConicalIcon size={16} className="text-[#3498dd]" data-id="element-97" />
                Qualitätsanalyse
              </h4>
              <div className="h-64 overflow-y-auto" data-id="element-98">
                <table className="min-w-full" data-id="element-99">
                  <thead className="bg-gray-50" data-id="element-100">
                    <tr className="text-xs text-gray-500" data-id="element-101">
                      <th className="px-3 py-2 text-left" data-id="element-102">Datum</th>
                      <th className="px-3 py-2 text-right" data-id="element-103">pH-Wert</th>
                      <th className="px-3 py-2 text-right" data-id="element-104">Viskosität</th>
                      <th className="px-3 py-2 text-left" data-id="element-105">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-106">
                    {qualityTests.map((test, index) => <tr key={index} className="text-xs" data-id="element-107">
                        <td className="px-3 py-2 text-gray-600" data-id="element-108">{test.date}</td>
                        <td className="px-3 py-2 text-right" data-id="element-109">
                          <span className={test.ph >= 7.0 && test.ph <= 7.4 ? 'text-green-600' : 'text-red-600'} data-id="element-110">
                            {test.ph}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right" data-id="element-111">
                          <span className={test.viscosity >= 950 && test.viscosity <= 1000 ? 'text-green-600' : 'text-red-600'} data-id="element-112">
                            {test.viscosity}
                          </span>
                        </td>
                        <td className="px-3 py-2" data-id="element-113">
                          <span className={`px-2 py-0.5 rounded text-xs ${test.consistency === 'Optimal' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`} data-id="element-114">
                            {test.consistency}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Tank Utilization Section - Update Ansatz tanks display */}
          <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6" data-id="element-115">
            <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-116">
              <DatabaseIcon size={16} className="text-[#3498dd]" data-id="element-117" />
              Tankauslastung (Zeitverlauf)
            </h4>
            <div className="space-y-8" data-id="element-118">
              {/* Ansatztanks */}
              <div data-id="element-119">
                <h5 className="text-xs font-medium text-gray-600 mb-3" data-id="element-120">
                  Ansatztanks
                </h5>
                <div className="space-y-6" data-id="element-121">
                  {Object.entries(tankUtilization.ansatzTanks).map(([tankId, values], index) => <div key={tankId} data-id="element-122">
                        <div className="flex justify-between text-xs text-gray-600 mb-2" data-id="element-123">
                          <span data-id="element-124">{tankId}</span>
                          <span data-id="element-125">
                            {values[values.length - 1].toFixed(1)}t/Tag
                          </span>
                        </div>
                        <div className="flex h-8 items-end gap-1" data-id="element-126">
                          {values.map((value, i) => <div key={i} className="flex-1 bg-[#3498dd] bg-opacity-15 rounded-t transition-all duration-300" style={{
                      height: `${value / Math.max(...values) * 100}%`
                    }} data-id="element-127" />)}
                        </div>
                        <div className="flex justify-between mt-1" data-id="element-128">
                          <span className="text-xs text-gray-400" data-id="element-129">
                            {tankUtilization.labels[0]}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-130">
                            {tankUtilization.labels[tankUtilization.labels.length - 1]}
                          </span>
                        </div>
                      </div>)}
                </div>
              </div>
              {/* Lagertanks */}
              <div data-id="element-131">
                <h5 className="text-xs font-medium text-gray-600 mb-3" data-id="element-132">
                  Lagertanks
                </h5>
                <div className="space-y-6" data-id="element-133">
                  {Object.entries(tankUtilization.lagerTanks).map(([tankId, values], index) => <div key={tankId} data-id="element-134">
                        <div className="flex justify-between text-xs text-gray-600 mb-2" data-id="element-135">
                          <span data-id="element-136">{tankId}</span>
                          <span data-id="element-137">
                            {values[values.length - 1].toFixed(1)}t/Tag
                          </span>
                        </div>
                        <div className="flex h-8 items-end gap-1" data-id="element-138">
                          {values.map((value, i) => <div key={i} className="flex-1 bg-[#3498dd] bg-opacity-15 rounded-t transition-all duration-300" style={{
                      height: `${value / Math.max(...values) * 100}%`
                    }} data-id="element-139" />)}
                        </div>
                        <div className="flex justify-between mt-1" data-id="element-140">
                          <span className="text-xs text-gray-400" data-id="element-141">
                            {tankUtilization.labels[0]}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-142">
                            {tankUtilization.labels[tankUtilization.labels.length - 1]}
                          </span>
                        </div>
                      </div>)}
                </div>
              </div>
            </div>
          </div>
          {/* Machine Efficiency Grid */}

          {/* Selected Orders Table */}
          <div className="mt-6" data-id="element-143">
            <h4 className="text-sm font-medium text-gray-700 mb-4" data-id="element-144">
              Einbezogene Aufträge
            </h4>
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-145">
              <table className="min-w-full" data-id="element-146">
                <thead className="bg-gray-50" data-id="element-147">
                  <tr className="text-xs text-gray-500" data-id="element-148">
                    <th className="px-4 py-2 text-left" data-id="element-149">Auftrag</th>
                    <th className="px-4 py-2 text-left" data-id="element-150">Charge</th>
                    <th className="px-4 py-2 text-left" data-id="element-151">Abschluss</th>
                    <th className="px-4 py-2 text-right" data-id="element-152">Menge</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-153">
                  {selectedOrders.map(order => <tr key={order.id} className="text-sm" data-id="element-154">
                      <td className="px-4 py-2 text-gray-900" data-id="element-155">{order.id}</td>
                      <td className="px-4 py-2 text-gray-600" data-id="element-156">
                        {order.charge}
                      </td>
                      <td className="px-4 py-2 text-gray-600" data-id="element-157">
                        {order.completionDate}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900" data-id="element-158">
                        {order.menge}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}